import { rehype } from 'rehype';
import rehypeFormat from 'rehype-format';
import Link from '@tiptap/extension-link';
import Color from '@tiptap/extension-color';
import Placeholder from '@tiptap/extension-placeholder';
import FontFamily from '@tiptap/extension-font-family';
import Code from '@tiptap/extension-code';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import Underline from '@tiptap/extension-underline';

import { urlPattern } from 'shared/constants';

import type { Editor, LoadExtensionsType } from './types';

import { baseExtensions, FileHandlerConfig, tipTapPluginsMode, TextAlignConfig } from './constants';
import { BackgroundColor, ImageExtension } from './extension';

export const formatHtml = (input: string): string =>
  String(rehype().use(rehypeFormat).processSync(input))
    .replace(/<\/?(html|head|body)>/g, '')
    .replace(/\n {4}/g, '\n')
    .replace(/^\s+|\s+$/g, '');

const protocols: string[] = ['http', 'https', 'ftp', 'mailto', 'file', 'data', 'irc'];
const urlPrefixRegex = new RegExp(`^(${protocols.join('|')}):`, 'i');
const uriPattern = /\w+:(\/?\/?)[^\s]+/i;

const validatetUrl = (newUrl: string): boolean => {
  const preparedValue = newUrl.trim().toLowerCase();
  const validUri = uriPattern.test(newUrl) && protocols.includes(preparedValue.split(':')[0]);

  return validUri && preparedValue.startsWith('http') ? urlPattern.test(newUrl) : validUri;
};

export const setLink = (editor: Editor) => {
  const previousUrl = editor.getAttributes('link').href;
  let url = window.prompt('URL', previousUrl);

  if (url === null) {
    return;
  }

  if (url === '') {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    return;
  }

  if (!urlPrefixRegex.test(url)) {
    url = `https://${url}`;
  }

  if (validatetUrl(url)) {
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }
};

export const loadExtensions = ({
  mode = tipTapPluginsMode.text,
  placeholder = 'Type something here...',
}: LoadExtensionsType) => {
  const extensions = [...baseExtensions];

  if (mode === tipTapPluginsMode.all || mode === tipTapPluginsMode.emailTemplates) {
    extensions.push(
      FontFamily,
      Color,
      BackgroundColor,
      TextAlignConfig,
      FileHandlerConfig,
      Underline,
      ImageExtension,
      Code,
      HorizontalRule,
    );
  }

  if (mode === tipTapPluginsMode.text) {
    extensions.push(FontFamily, Color, BackgroundColor, TextAlignConfig, FileHandlerConfig, Underline);
  }

  extensions.push(
    Placeholder.configure({
      placeholder,
    }),
  );

  extensions.push(
    Link.configure({
      linkOnPaste: true,
      openOnClick: true,
      autolink: false,
      protocols: ['ftp', 'mailto', 'https'],
    }),
  );

  return extensions;
};
